import {VehicleType} from "../../../../generated/graphql";
import {ManifestStatusType} from "../../../settings/ColorSettings/ColorizedIndicators/types/manifestAttributes";
import {TChangedDispatchGroupState} from "../../common/components/DispatchGroupsSeletor";
import {TDriverItem} from "../CreateManifestModal";
import styled from "@emotion/styled";
import {Button, Icon, Text, Tooltip} from "@blueprintjs/core";
import VehicleSelector from "../../common/components/VehicleSelector";
import {getEllipsizeText, shouldEllipsize} from "../../../../utils/General";
import ManifestStatusSeletor from "../../common/components/ManifestStatusSeletor";
import {EmptyValueStrings} from "../../../../constants/constants";
import {SwapIcon} from "../../../common/icons";
import {TDriverEnhanced} from "../../../common/types/enhanced";

type OptionItemProps = {
  index: number;
  item: TDriverEnhanced;
  vehicleTypes: VehicleType[];
  isHighlight: boolean;
  changedDispatchGroupState: TChangedDispatchGroupState | undefined;
  onStatusSelect: (driverId: number, status: ManifestStatusType) => void;
  selectedStatus: ManifestStatusType;
  onVehicleSelect: (driverId: number, vehicleId: number) => void;
  selectedVehicleId: number | null;
  onClick: (item: TDriverItem) => void;
  onEditClick: (item: TDriverEnhanced) => void;
};

const DriverItem = ({
  item,
  index,
  vehicleTypes,
  isHighlight,
  changedDispatchGroupState,
  onStatusSelect,
  selectedStatus,
  onVehicleSelect,
  selectedVehicleId,
  onClick,
  onEditClick
}: OptionItemProps) => {
  const handleOnStatusSelect = (status: ManifestStatusType) => {
    onStatusSelect(item.driverId!, status);
  };

  const handleOnVehicleSelect = (vehicle: VehicleType) => {
    onVehicleSelect(item.driverId!, vehicle.vehicleTypeId);
  };

  return (
    <Option
      data-testid="driver-item"
      index={index}
      key={item.driverId}
      onClick={() =>
        onClick({
          ...item,
          dispatchGroupId: changedDispatchGroupState?.key ?? item.dispatchGroupId,
          manifestDriverStatus: selectedStatus,
          vehicleTypeId: selectedVehicleId
        })
      }
      isHighlight={isHighlight}
    >
      <div></div>
      <StyledButton
        onClick={(e) => {
          e.stopPropagation();
        }}
      >
        <ManifestStatusSeletor selectedStatus={selectedStatus} onSelectStatus={handleOnStatusSelect} minimal />
      </StyledButton>
      <Tooltip
        content={item.phone ?? EmptyValueStrings.notProvided}
        position="top"
        targetProps={{style: {width: "fit-content", marginBottom: "2px"}}}
      >
        <Icon icon="phone" color="#797979" size={12} />
      </Tooltip>
      <StyledButton
        onClick={(e) => {
          e.stopPropagation();
        }}
      >
        <VehicleSelector
          items={vehicleTypes}
          onItemSelect={handleOnVehicleSelect}
          selectedItem={vehicleTypes.find((vt) => vt.vehicleTypeId === selectedVehicleId)}
          options={{dropdownIcon: "caret-down", dropdownIconSize: 14, scrollToActiveItem: false}}
        />
      </StyledButton>
      <Tooltip
        popoverClassName="white-popover"
        content={shouldEllipsize(item.name, 19) ? (item.name as string) : ""}
        placement="bottom-start"
      >
        <BlackText>{getEllipsizeText(item.name, 19)}</BlackText>
      </Tooltip>
      <Tooltip
        popoverClassName="white-popover"
        content={shouldEllipsize(item.driverCode, 10) ? (item.driverCode as string) : ""}
        placement="bottom-start"
      >
        <GrayText>{getEllipsizeText(item.driverCode, 10)}</GrayText>
      </Tooltip>
      <DispatchGroupDetail>
        <Tooltip
          popoverClassName="white-popover"
          content={
            shouldEllipsize(changedDispatchGroupState?.value ?? item.dispatchGroup?.dispatchGroup, 10)
              ? changedDispatchGroupState?.value ?? item.dispatchGroup?.dispatchGroup ?? EmptyValueStrings.unknown
              : ""
          }
          placement="bottom-start"
        >
          <BlackText ellipsize>
            {getEllipsizeText(changedDispatchGroupState?.value ?? item.dispatchGroup?.dispatchGroup, 10)}
          </BlackText>
        </Tooltip>
        {changedDispatchGroupState && (
          <Tooltip
            popoverClassName="white-popover"
            position="top"
            content={`Changed from '${item.dispatchGroup?.dispatchGroup ?? EmptyValueStrings.unknown}'`}
          >
            <Icon icon={<SwapIcon />} />
          </Tooltip>
        )}
      </DispatchGroupDetail>
      <Tooltip minimal popoverClassName="white-popover" placement="bottom-end" content={"Change driver's group"}>
        <EditButton
          data-testid="edit-button"
          variant="minimal"
          icon={<Icon icon={"more"} style={{rotate: "90deg"}} />}
          onClick={(e) => {
            e.stopPropagation();
            onEditClick(item);
          }}
        />
      </Tooltip>
    </Option>
  );
};

export default DriverItem;

const Option = styled.li<{index: number; isHighlight: boolean}>`
  box-sizing: border-box;
  background-color: ${(props) => (props.isHighlight ? "#0038ff33" : props.index % 2 === 0 ? "#ffffff" : "#FCFCFC")};
  height: 43px;
  color: #161616;
  font-size: 14px;
  border-bottom: 1px solid #eeeeee;
  border-top: ${(props) => (props.index === 0 ? "1px solid #eeeeee" : "")};
  display: grid;
  grid-template-columns: 16px 35px 16px 50px 1.2fr 1fr 1.2fr 40px;
  align-items: center;
  justify-content: center;
  gap: 4px;

  :hover {
    background-color: #0038ff33;
    cursor: pointer;
  }
`;

const BlackText = styled(Text)`
  width: 100%;
  display: flex;
  align-items: center;
`;

const GrayText = styled(Text)`
  width: 100%;
  display: flex;
  align-items: center;
  color: #797979;
`;

const EditButton = styled(Button)`
  width: 20px;
  height: 20px;
`;

const StyledButton = styled(Button)`
  background: none !important;
  box-shadow: none !important;
`;

const DispatchGroupDetail = styled.div`
  display: grid;
  grid-template-columns: auto 1fr;
  align-items: center;
  gap: 6px;
  overflow: hidden;

  .bp5-popover-target {
    display: flex;
    align-items: center;
    width: fit-content;
  }
`;
